<template>
    <div
        ref="resultRef"
        :class="[isSearchOpened && $style.search_opened]"
    >
        <div :class="$style.search">
            <TextInput
                id="search"
                name="search"
                color="purple"
                :visual="props.visual"
                type="search"
                :paddingSize="props.paddingSize"
                prefixIcon="search"
                :placeholder="$tr('search.placeholder')"
                :class="[inputClass, $style.search_input]"
                autocomplete="off"
                @focus="onOpen"
                @update:modelValue="onInput"
            />
            <Button
                v-if="isSearchOpened"
                :class="$style.close_btn"
                interfaceTarget="close-search"
                icon="close"
                color="carbon-400"
                @click="onClose"
            />
        </div>
        <template v-if="isSearchOpened">
            <div
                :class="$style.overlay_blur"
                @click="onClose"
            />
            <div :class="$style.resultsAnchor">
                <SearchResults
                    :searchResults="searchData"
                    :status
                />
            </div>
        </template>
    </div>
</template>
<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import Button from '@/components_new/Button.vue'
import TextInput from '@/components_new/TextInput/TextInput.vue'
import { useConfig } from '@/composables/config'
import { useI18nAddon } from '@/i18n/i18n'
import { useLayoutStore } from '@/store/layout-store'
import { useLoggerStore } from '@/store/logger'

import * as api from './api'
import SearchResults, { SearchStatusResult } from './components/SearchResults.vue'

const props = withDefaults(
    defineProps<{
        visual?: 'rounded'
        paddingSize?: 's' | 'm'
        inputClass?: string
    }>(),
    {
        visual: 'rounded',
        paddingSize: 's',
    },
)

const resultRef = ref<HTMLElement | null>(null)
const inputValue = ref('')
const isSearchOpened = ref(false)
const loggerStore = useLoggerStore()
const layoutStore = useLayoutStore()
const emit = defineEmits<{ open: []; close: [] }>()

const i18nAddon = useI18nAddon()
const router = useRouter()
const { GOOGLE_SEARCH_ID, GOOGLE_SEARCH_API_TOKEN } = useConfig()

function toggleSearch(value: boolean) {
    if (value === isSearchOpened.value) {
        return
    }
    isSearchOpened.value = value
    layoutStore.setFullscreen(value)
}
router.afterEach(onClose)
function onOpen() {
    toggleSearch(true)
    emit('open')
}
function onClose() {
    toggleSearch(false)
    emit('close')
}
const queryEnabled = computed(() => inputValue.value.length > 1)

const onInput = useDebounceFn((value: string) => {
    inputValue.value = value.trim()
}, 500)

const status = computed((): SearchStatusResult => {
    if ((searchData.value && searchData.value.result.length === 0) || isError.value) {
        return 'empty'
    } else if (searchData.value) {
        return 'loaded'
    } else if (isLoading.value && inputValue.value) {
        return 'loading'
    }
    return 'initial'
})

const {
    isError,
    isLoading,
    isSuccess,
    data: searchData,
} = useQuery({
    queryKey: ['portal-search', inputValue, i18nAddon.currentLocale],
    enabled: queryEnabled,
    queryFn: () => {
        return api.doSearch({
            searchID: GOOGLE_SEARCH_ID,
            apiKey: GOOGLE_SEARCH_API_TOKEN,
            q: inputValue.value,
            locale: i18nAddon.currentLocale.value,
            lang: i18nAddon.getCurrentLanguage(),
            resolveRoute(route: string) {
                return router.resolve(route)
            },
        })
    },
    staleTime: Infinity,
    // we try to economy the search requests
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
})

watch(isSuccess, (success) => {
    if (success && searchData.value) {
        loggerStore.logSearchEvent({
            search_result_count: searchData.value.totalItems,
            search_term: searchData.value.queryTerm,
        })
    }
})

function onClickOutside(event: MouseEvent) {
    if (!isSearchOpened.value) {
        return
    }

    const target = event.target as HTMLElement

    const isClickInsideResultSearch = resultRef.value?.contains(target)
    // if click was outside searchRef, but input still in focus — don't close
    const isInputFocused = resultRef.value?.contains(document.activeElement)

    if (!isInputFocused && !isClickInsideResultSearch) {
        onClose()
    }
}

function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && isSearchOpened.value) {
        const activeElement = document.activeElement as HTMLElement
        onClose()
        activeElement.blur()
    }
}

onMounted(() => {
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeyDown)
    document.removeEventListener('click', onClickOutside)
})
</script>
<style module>
.search_opened {
    width: 100%;
    height: 100vh;
    box-sizing: border-box;
    z-index: 1;
    overflow: auto;
    scrollbar-width: none;
    background-color: var(--black);
    position: fixed;
    top: var(--global-page-top-padding);
    padding-bottom: env(safe-area-inset-bottom);
    left: 0;
    right: 0;
}
.resultsAnchor {
    padding-top: 16px;
    width: 100%;
}

.close_btn {
    margin-left: 4px;
}

.search {
    display: flex;
}
.overlay_blur {
    display: none;
}

.search_opened .search,
.search_opened .search_input {
    width: 100%;
    flex-grow: 1;
}

.search_opened .search,
.resultsAnchor {
    box-sizing: border-box;
    padding-left: calc(var(--global-padding-horizontal) + env(safe-area-inset-left));
    padding-right: calc(var(--global-padding-horizontal) + env(safe-area-inset-right));
}

@media (--desktop-small-plus) {
    .search_opened {
        position: relative;
        width: 100%;
        height: auto;
        padding: 0;
        top: 0;
        z-index: initial;
        overflow: visible;
        scrollbar-width: initial;
        background-color: inherit;
    }

    .resultsAnchor {
        background-color: var(--carbon-400);
        padding: 12px 0;
        position: absolute;
        overflow: auto;
        scrollbar-width: none;
        margin-top: var(--global-gap);
        border-radius: 16px;
        height: 650px;
        z-index: 1;
    }
    .search_opened .search {
        padding: 0;
    }
    .close_btn {
        display: none;
    }
    .overlay_blur {
        display: block;
        position: fixed;
        width: 100vw;
        height: 100vh;
        left: 0;
        top: calc(var(--global-header-pannel-height) + var(--global-gap) * 2);
        z-index: 1;
        backdrop-filter: blur(20px);
    }
    .search {
        display: block;
    }
}
</style>
